import React, { useCallback, useMemo, useState, memo } from 'react';
import Icon from 'choerodon-ui/lib/icon';
import difference from 'lodash/difference';
import TextField from '../../text-field';
import CheckBox from '../../check-box';
import { $l } from '../../locale-context';
const FieldList = function FieldList({ value, onSelect, onUnSelect, groups, prefixCls }) {
    const [searchText, setSearchText] = useState('');
    const codes = useMemo(() => groups.reduce((res, current) => [...res, ...current.fields.map((o) => {
            const hasBindProps = (propsName) => o && o.get(propsName) && o.get(propsName).bind;
            if (!o.get('bind') &&
                !hasBindProps('computedProps') &&
                !hasBindProps('dynamicProps')) {
                return o.get('name');
            }
            return undefined;
        })], []), [groups]);
    const labelCodes = useMemo(() => groups.reduce((res, current) => [...res, ...current.fields.map((o) => {
            const hasBindProps = (propsName) => o && o.get(propsName) && o.get(propsName).bind;
            if (!o.get('bind') &&
                !hasBindProps('computedProps') &&
                !hasBindProps('dynamicProps')) {
                return [o.get('name'), o.get('label')];
            }
            return undefined;
        })], []), [groups]);
    const hasSelect = useMemo(() => value.length > 0, [value.length]);
    const hasSelectAll = useMemo(() => difference(codes, value).length === 0, [codes.length, value.length]);
    const isChecked = useCallback((code) => value.includes(code), [value]);
    const handleChange = useCallback((code, select) => {
        if (select) {
            onSelect(code);
        }
        else {
            onUnSelect(code);
        }
    }, [onSelect, onUnSelect]);
    const renderGroup = useCallback((group) => group.fields.length > 0 && (React.createElement("div", { className: `${prefixCls}-section`, key: group.title },
        React.createElement("div", { className: `${prefixCls}-list` }, group.fields.map((field) => {
            const code = field.get('name');
            const label = field.get('label') || code;
            const checked = isChecked(code);
            const hasBindProps = (propsName) => field && field.get(propsName) && field.get(propsName).bind;
            if (label && label.includes(searchText || '') &&
                !field.get('bind') &&
                !hasBindProps('computedProps') &&
                !hasBindProps('dynamicProps')) {
                return (React.createElement("div", { className: `${prefixCls}-item`, key: code },
                    React.createElement(CheckBox, { value: code, checked: checked, onChange: () => handleChange(code, !checked) }, label)));
            }
            return null;
        })))), [handleChange, isChecked, searchText]);
    const selectItems = value.filter(v => codes.includes(v));
    return (React.createElement("div", { className: prefixCls },
        React.createElement("div", { className: `${prefixCls}-search` },
            React.createElement(TextField, { style: { flex: 1 }, value: searchText, onClick: (e) => {
                    e.preventDefault();
                    e.currentTarget.focus();
                }, onChange: (v) => {
                    setSearchText(v);
                }, onInput: (e) => {
                    setSearchText(e.target.value);
                }, prefix: React.createElement(Icon, { type: "search" }), placeholder: $l('Table', 'enter_text_filter'), clearButton: true })),
        React.createElement("div", { className: `${prefixCls}-header` },
            React.createElement("span", { className: `${prefixCls}-search-selected` },
                $l('Screening', 'selected'),
                React.createElement("span", { className: `${prefixCls}-search-items` }, selectItems.length),
                $l('Transfer', 'items')),
            React.createElement("span", { className: `${prefixCls}-search-divide` }),
            React.createElement("span", { style: { display: !hasSelectAll ? 'inline-block' : 'none' }, className: `${prefixCls}-search-action`, onClick: () => {
                    const values = labelCodes.map(lc => {
                        if (lc && (lc[1] && lc[1].includes(searchText || '') || lc[1] === undefined)) {
                            return lc[0];
                        }
                        return undefined;
                    }).filter(v => !!v);
                    handleChange(values, true);
                } }, $l('Select', 'select_all')),
            React.createElement("span", { style: { display: hasSelect ? 'inline-block' : 'none' }, className: `${prefixCls}-search-action`, onClick: () => {
                    const values = labelCodes.map(lc => {
                        if (lc && (lc[1] && lc[1].includes(searchText || '') || lc[1] === undefined)) {
                            return lc[0];
                        }
                        return undefined;
                    }).filter(v => !!v);
                    handleChange(values, false);
                } }, $l('Table', 'clear_filter'))),
        React.createElement("div", { className: `${prefixCls}-content` }, groups.map((group) => renderGroup(group)))));
};
FieldList.displayName = 'FieldList';
export default memo(FieldList);
